import { LocalStorageAdapter } from '../adapters/LocalStorageAdapter.js'
import type { MatchResult } from '../interfaces/index.js'
import type { ReplayData } from './ReplayRecorder.js'

const REPLAY_KEY = 'ultimaze_replays'
const MAX_REPLAYS = 10

interface StoredReplayEntry {
  matchId: string
  result: MatchResult
  replay: ReplayData
}

export class ReplayService {
  private static instance: ReplayService
  private adapter = new LocalStorageAdapter()

  static getInstance(): ReplayService {
    if (!ReplayService.instance) ReplayService.instance = new ReplayService()
    return ReplayService.instance
  }

  async getMatchHistory(): Promise<MatchResult[]> {
    return this.adapter.getRecentMatches(20)
  }

  // ── Replay storage ──────────────────────────────────────────────────────────

  /**
   * Persist replay data alongside its MatchResult.
   * Silently skips if localStorage is full.
   */
  async saveReplay(matchResult: MatchResult, replayData: ReplayData): Promise<void> {
    try {
      const stored = this._loadReplays()
      stored.unshift({ matchId: matchResult.timestamp, result: matchResult, replay: replayData })
      if (stored.length > MAX_REPLAYS) stored.length = MAX_REPLAYS
      localStorage.setItem(REPLAY_KEY, JSON.stringify(stored))
    } catch {
      /* storage full — silently skip */
    }
  }

  /**
   * Load the ReplayData for a specific match by timestamp.
   * Returns null if no replay is stored for that match.
   */
  async getReplayForMatch(timestamp: string): Promise<ReplayData | null> {
    const stored = this._loadReplays()
    return stored.find((r) => r.matchId === timestamp)?.replay ?? null
  }

  /**
   * Returns all recent matches, each annotated with whether a replay is available.
   */
  async getMatchesWithReplay(): Promise<Array<{ result: MatchResult; hasReplay: boolean }>> {
    const matches = await this.getMatchHistory()
    const stored = this._loadReplays()
    const replayIds = new Set(stored.map((r) => r.matchId))
    return matches.map((r) => ({ result: r, hasReplay: replayIds.has(r.timestamp) }))
  }

  // ── Formatting helpers ──────────────────────────────────────────────────────

  formatDuration(secs: number): string {
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return m + ':' + String(s).padStart(2, '0')
  }

  /** Format milliseconds as MM:SS */
  formatMs(ms: number): string {
    return this.formatDuration(Math.floor(ms / 1000))
  }

  getOutcomeColor(outcome: string): string {
    if (outcome === 'win') return '#4CAF50'
    if (outcome === 'lose') return '#E53935'
    return '#9E9E9E'
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private _loadReplays(): StoredReplayEntry[] {
    try {
      return JSON.parse(localStorage.getItem(REPLAY_KEY) ?? '[]') as StoredReplayEntry[]
    } catch {
      return []
    }
  }
}
